// src/shared/components/feedback/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useError, ErrorType } from '@/shared/contexts/ErrorContext';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to the console in development
    // Using Vite's import.meta.env.DEV instead of process.env.NODE_ENV
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render the fallback UI if provided, otherwise render a default error message
      return this.props.fallback || (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <h2 className="text-lg font-semibold text-red-800">Something went wrong</h2>
          <p className="mt-2 text-sm text-red-700">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component to inject the error context
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const { addError } = useError();

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    // Add the error to the global error context using the simplified structure
    addError({
      type: ErrorType.SYSTEM,
      message: error.message,
      details: { componentStack: errorInfo.componentStack }
    });

    // Call the original onError if provided
    if (props.onError) {
      props.onError(error, errorInfo);
    }
  };

  return <ErrorBoundaryComponent {...props} onError={handleError} />;
};

export default ErrorBoundary;
